import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import axios from "axios";
import Cookies from "js-cookie";
import classNames from 'classnames';
import { Camera, Calendar, RefreshCw, Image, Upload, Tag, Search, X, ArrowUpDown } from "lucide-react";
import './ListaMedios.css';

// Componentes
import MedioCardGrid from "./MedioCardGrid.jsx";
import MedioCardList from "./MedioCardList.jsx";
import MedioCardSkeleton from './MedioCardSkeleton.jsx'; // Necesitarás crear este componente
import DropdownOrder from "../../../Store/DropdownOrder.jsx";
import DropdownTipoVista from "../../../Store/DropdownTipoVista.jsx";
import BuscadorInput from "../../../Store/BuscadorInput.jsx";
import DropdownAddMedio from "../../components/ListaDirecciones/components/DropdownAddMedio.jsx";

// Configuración inicial
const INITIAL_VIEW_STATE = {
    mode: "grid",
    columns: 3,
    sortBy: 'created_at',
    sortDirection: 'desc',
};

const SORT_OPTIONS = [
    {
        nombre: "Nombre",
        valor: "nombre",
        icon: <Tag className="w-4 h-4" />
    },
    {
        nombre: "Fecha de Creación",
        valor: "created_at",
        icon: <Calendar className="w-4 h-4" />
    },
    {
        nombre: "Fecha de Modificación",
        valor: "updated_at",
        icon: <Calendar className="w-4 h-4" />
    },
];

const VIEW_OPTIONS = [
    { label: '2 Columnas', value: { mode: 'grid', columns: 2 } },
    { label: '3 Columnas', value: { mode: 'grid', columns: 3 } },
    { label: '4 Columnas', value: { mode: 'grid', columns: 4 } },
    { label: 'Lista', value: { mode: 'list' } },
];

// Hook para debounce de búsqueda
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
};

const ListaMedios = ({ setEditor, setEditorType, setEditorID }) => {
    // Estados principales
    const [mediaList, setMediaList] = useState([]);
    const [totalCount, setTotalCount] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Estado de vista con persistencia
    const [viewMode, setViewMode] = useState(() => {
        const savedViewMode = Cookies.get('viewModeMedios');
        return savedViewMode ? JSON.parse(savedViewMode) : INITIAL_VIEW_STATE;
    });

    // Debounce para búsqueda
    const debouncedSearchTerm = useDebounce(searchTerm, 300);

    // Función principal de fetch
    const fetchMedios = useCallback(async (pageNum = 1, reset = false) => {
        try {
            setLoading(true);
            setError(null);

            const { data } = await axios.get("/media/", {
                params: {
                    page: pageNum,
                    sortBy: viewMode.sortBy,
                    sortDirection: viewMode.sortDirection,
                    search: debouncedSearchTerm || undefined
                }
            });

            setMediaList(prev =>
                reset || pageNum === 1 ? data.results : [...prev, ...data.results]
            );
            setHasMore(Boolean(data.next));
            setTotalCount(data.count ?? null);
        } catch (err) {
            console.error("Error fetching medios:", err);
            setError("Error al cargar los medios");
        } finally {
            setLoading(false);
        }
    }, [viewMode.sortBy, viewMode.sortDirection, debouncedSearchTerm]);

    // Intersection Observer para scroll infinito
    const observer = useRef();
    const lastItemRef = useCallback(node => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !loading) {
                setPage(prev => {
                    const nextPage = prev + 1;
                    fetchMedios(nextPage, false);
                    return nextPage;
                });
            }
        }, { threshold: 0.1 });

        if (node) observer.current.observe(node);
    }, [loading, hasMore, fetchMedios]);

    // Efectos
    useEffect(() => {
        setPage(1);
        fetchMedios(1, true);
    }, [viewMode, debouncedSearchTerm]);

    useEffect(() => {
        Cookies.set('viewModeMedios', JSON.stringify(viewMode), { expires: 7 });
    }, [viewMode]);

    // Handlers para añadir medios
    const handleAddFromFile = (() => {
        setEditorType('medios_svg_input');
        setEditorID("add");
        setEditor(true);
    });

    const handleAddFromImage = useCallback(() => {
        setEditorType('medios_img_input');
        setEditorID("add");
        setEditor(true);
    }, [setEditor, setEditorType, setEditorID]);

    const handleDelete = useCallback((medioId) => {
        setMediaList(prev => prev.filter(medio => medio.id !== medioId));
    }, []);

    const handleRetry = useCallback(() => {
        setError(null);
        setPage(1);
        fetchMedios(1, true);
    }, [fetchMedios]);

    const handleClearSearch = useCallback(() => {
        setSearchTerm('');
    }, []);

    // Función para refrescar la lista después de crear/editar
    const refreshMedios = useCallback(() => {
        setPage(1);
        fetchMedios(1, true);
    }, [fetchMedios]);

    // Estilos memoizados para el grid
    const gridClassName = useMemo(() => {
        return viewMode.mode === "grid" ? "grid gap-4" : "flex flex-col space-y-4";
    }, [viewMode.mode]);

    const gridStyle = useMemo(() => {
        return viewMode.mode === "grid"
            ? { gridTemplateColumns: `repeat(${viewMode.columns}, minmax(0, 1fr))` }
            : {};
    }, [viewMode.mode, viewMode.columns]);

    // Estados de UI
    const showEmptyState = mediaList.length === 0 && !loading && !error;
    const showSkeletons = loading && mediaList.length === 0;
    const hasActiveFilters = debouncedSearchTerm || viewMode.sortBy !== 'created_at';

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">
            {/* Header de controles */}
            <div className="glassCard p-4 z-40">
                {/* Título y contador */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <Image className="w-6 h-6 text-primary" />
                        <div>
                            <h2 className="text-xl font-semibold">Medios</h2>
                            <p className="text-sm text-base-content/60">
                                {totalCount !== null
                                    ? `Mostrando ${mediaList.length} de ${totalCount} ${totalCount === 1 ? 'medio' : 'medios'}`
                                    : `${mediaList.length} ${mediaList.length === 1 ? 'medio' : 'medios'}`
                                }
                            </p>
                        </div>
                    </div>

                    {/* Botones de añadir */}
                    <div className="flex gap-2">
                        {/* Dropdown para añadir medios */}
                        <DropdownAddMedio
                            setEditor={setEditor}
                            setEditorID={setEditorID}
                            setEditorType={setEditorType}
                            className={"w-full sm:w-auto"}
                        />
                    </div>
                </div>

                {/* Controles */}
                <div className="flex flex-col-reverse lg:flex-row gap-2">
                    {/* Sección izquierda - Búsqueda */}
                    <div className="flex-1 lg:flex-initial min-w-64 lg:min-w-48 lg:max-w-md">
                        <BuscadorInput
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            className="w-full"
                            placeholder="Buscar medios..."
                        />
                    </div>

                    {/* Sección derecha - Controles de vista */}
                    <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-row lg:items-center lg:gap-2">
                        <DropdownOrder
                            viewMode={viewMode}
                            setViewMode={setViewMode}
                            arrayDatosOrdenacion={SORT_OPTIONS}
                            className="w-full"
                        />

                        <DropdownTipoVista
                            options={VIEW_OPTIONS}
                            selectedValue={viewMode}
                            onChange={(mode) => setViewMode(prev => ({
                                ...prev,
                                mode: mode.mode,
                                columns: mode.columns
                            }))}
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Indicador de filtros activos */}
                {hasActiveFilters && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">

                        {debouncedSearchTerm && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1
                                             text-xs font-medium rounded-full
                                             bg-primary/10 text-primary border border-primary/20">
                                <Search className="w-3 h-3 shrink-0" />
                                <span className="max-w-[160px] truncate">"{debouncedSearchTerm}"</span>
                                <button
                                    onClick={handleClearSearch}
                                    className="ml-0.5 hover:text-error transition-colors"
                                    aria-label="Quitar filtro de búsqueda"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        )}

                        {viewMode.sortBy !== 'created_at' && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1
                                             text-xs font-medium rounded-full
                                             bg-base-300 text-base-content/70 border border-base-300">
                                <ArrowUpDown className="w-3 h-3 shrink-0" />
                                <span>{SORT_OPTIONS.find(o => o.valor === viewMode.sortBy)?.nombre ?? 'Orden'}</span>
                                <button
                                    onClick={() => setViewMode(prev => ({ ...prev, sortBy: 'created_at', sortDirection: 'desc' }))}
                                    className="ml-0.5 hover:text-error transition-colors"
                                    aria-label="Restablecer orden"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        )}

                    </div>
                )}
            </div>

            {/* Contenido principal */}
            <div className={classNames(
                "h-full flex-1 overflow-y-auto scrollbar_mod glassCard",
                showEmptyState || error ? "flex items-center justify-center" : "p-4"
            )}>
                {/* Estado de error */}
                {error && (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-4 bg-error/10 rounded-full flex items-center justify-center">
                            <RefreshCw className="w-8 h-8 text-error" />
                        </div>
                        <h3 className="text-lg font-medium text-base-content mb-2">
                            Error al cargar medios
                        </h3>
                        <p className="text-base-content/60 mb-4">{error}</p>
                        <button
                            onClick={handleRetry}
                            className="btn btn-outline"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reintentar
                        </button>
                    </div>
                )}

                {/* Estado vacío */}
                {showEmptyState && (
                    <div className="text-center py-12">
                        <div className="w-20 h-20 mx-auto mb-6 bg-base-200 rounded-full flex items-center justify-center">
                            <Camera className="w-10 h-10 text-base-content/40" />
                        </div>

                        {debouncedSearchTerm ? (
                            <>
                                <h3 className="text-lg font-medium text-base-content mb-2">
                                    No se encontraron medios
                                </h3>
                                <p className="text-base-content/60 mb-6">
                                    No hay medios que coincidan con "{debouncedSearchTerm}"
                                </p>
                                <button
                                    onClick={handleClearSearch}
                                    className="btn btn-outline"
                                >
                                    Limpiar búsqueda
                                </button>
                            </>
                        ) : (
                            <>
                                <h3 className="text-lg font-medium text-base-content mb-2">
                                    No tienes medios aún
                                </h3>
                                <p className="text-base-content/60 mb-6">
                                    Comienza añadiendo tu primer medio
                                </p>
                                <div className="flex gap-2 justify-center">
                                    <button onClick={handleAddFromFile} className="btn btn-primary">
                                        <Upload className="w-5 h-5 mr-2" />
                                        Desde Archivo
                                    </button>
                                    <button onClick={handleAddFromImage} className="btn btn-secondary">
                                        <Image className="w-5 h-5 mr-2" />
                                        Desde Imagen
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Grid de contenido */}
                {!error && !showEmptyState && (
                    <div className={gridClassName} style={gridStyle}>
                        {/* Skeletons durante carga inicial */}
                        {showSkeletons &&
                            Array.from({ length: viewMode.columns * 2 }).map((_, i) => (
                                <MedioCardSkeleton key={`skeleton-${i}`} />
                            ))
                        }

                        {/* Medios reales */}
                        {mediaList.map((medio, idx) => {
                            const isLast = idx === mediaList.length - 1;
                            const Card = viewMode.mode === "grid" ? MedioCardGrid : MedioCardList;

                            return isLast ? (
                                <div ref={lastItemRef} key={medio.id}>
                                    <Card
                                        media={medio}
                                        setEditor={setEditor}
                                        setEditorID={setEditorID}
                                        setEditorType={setEditorType}
                                        onDelete={() => handleDelete(medio.id)}
                                        refreshMedios={refreshMedios}
                                    />
                                </div>
                            ) : (
                                <Card
                                    key={medio.id}
                                    media={medio}
                                    setEditor={setEditor}
                                    setEditorID={setEditorID}
                                    setEditorType={setEditorType}
                                    onDelete={() => handleDelete(medio.id)}
                                    refreshMedios={refreshMedios}
                                />
                            );
                        })}

                        {/* Indicador de carga al final */}
                        {loading && mediaList.length > 0 && (
                            <div className="col-span-full flex justify-center py-4">
                                <div className="flex items-center space-x-2 text-base-content/60">
                                    <div className="loading loading-spinner loading-sm"></div>
                                    <span className="text-sm">Cargando más medios...</span>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Mensaje de fin de contenido */}
                {!hasMore && mediaList.length > 0 && !loading && (
                    <div className="text-center py-6 text-base-content/60">
                        <p className="text-sm">Has visto todos los medios disponibles</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ListaMedios;